import { Field, Rule } from 'pods/forms/FormBuilder.d';
import { ADDRESS, MANUAL_ADDRESS, STATE_POSTCODE } from 'core/constants/form';
import {
	getMessageByCode,
	JssTextType,
	SitecoreErrorMsgItemType,
} from 'core/utils/sitecore-utils';
import { CONTACT_DETAILS_ERRORS } from 'core/constants';

import countries from './countries.json';
import states from './states.json';

export const AUSTRALIA_COUNTRY_CODE = 'AUS';

interface IPostCodeErrorMessages {
	au: string;
	intel: string;
}

interface IGenerateAddressFieldsConfig {
	/** Address path used to group input value together */
	addressPathPrefix: string;
	/** Address auto complete input field label */
	addressLabel: JssTextType | string;
	/** Street address 1 input field label */
	street1Label: JssTextType | string;
	/** Street address 2 input field label */
	street2Label: JssTextType | string;
	/** Suburb input field label */
	suburbLabel: JssTextType | string;
	/** State input field label */
	stateLabel: JssTextType | string;
	/** Country select field label */
	countryLabel: JssTextType | string;
	/** Postcode input field label */
	postcodeLabel: JssTextType | string;
	/** Address fieldset legend */
	addressFieldsetLegend: JssTextType | string;
	/** Additional address checkbox label */
	additionalAddressLabel?: JssTextType | string;
	/** DO NOT PROVIDE this param for the additional address fields */
	/** NOTE: this will be auto generated for additional address fields */
	rules?: Rule[];
	/** Address error message from Sitecore */
	addressInvalidErrorMessage?: string;
	/** Postalcode error message from Sitecore (ER09) */
	postcodeErrorMessages?: IPostCodeErrorMessages;
	/** Global Error messages */
	errorMessages: SitecoreErrorMsgItemType[];
	/** Max length: AU postcode = 4 digits, International postcode = 20 alphanumeric */
	postalCodeMaxLength?: number;
	/** label used for address auto complete when no result is returned */
	emptyResultLabel: JssTextType;
	/** label used for address auto complete when user cannot find matching address in the result */
	noMatchLabel: JssTextType;
}

/**
 * Generate address auto complete and manual enter fields.
 *
 * @param primaryAddress    - primary address config
 * @param additionalAddress - optional additional address config and only show when additionalAddress checkbox is check
 * @param isFormDisabled - this is to disable all the form fields. Helpful in preventing the user to hit enter key while form is still submitting to avoid mutliple form submission
 * @example
 * // addressPathPrefix = 'homeAddress'
 * // formValues will be
 * {
 *    'homeAddress': {
 *      address: 'foo',
 *      street1: 'bar',
 *    }
 * }
 */
export const generateAddressFields = (
	primaryAddress: IGenerateAddressFieldsConfig,
	additionalAddress?: IGenerateAddressFieldsConfig,
	isFormDisabled = false,
): Field<never, never>[] => {
	const {
		stateLabel,
		suburbLabel,
		countryLabel,
		addressLabel,
		street1Label,
		street2Label,
		postcodeLabel,
		addressPathPrefix,
		addressFieldsetLegend,
		additionalAddressLabel,
		rules,
		addressInvalidErrorMessage,
		errorMessages,
		emptyResultLabel,
		noMatchLabel,
		postcodeErrorMessages,
	} = primaryAddress;

	if (!additionalAddress) {
		return [
			{
				id: addressPathPrefix,
				type: 'sub-layout',
				data: {
					layout: '66%, 100%',
					isFieldset: true,
					legend: addressFieldsetLegend,
					gap: 0,
				},
				fields: [
					{
						id: `${addressPathPrefix}.${ADDRESS.COUNTRY}`,
						path: `${addressPathPrefix}.${ADDRESS.COUNTRY}`,
						type: 'select',
						required: true,
						requiredMessage: addressInvalidErrorMessage,
						disabled: isFormDisabled,
						data: {
							options: countries,
							label: countryLabel,
						},
						validators: [
							{
								type: 'required',
								message: addressInvalidErrorMessage,
							},
						],
					},
					{
						id: ADDRESS.ADDRESS_LOOKUP,
						type: 'section',
						fields: [
							{
								id: `${addressPathPrefix}.${ADDRESS.ADDRESS_LOOKUP}`,
								path: `${addressPathPrefix}.${ADDRESS.ADDRESS_LOOKUP}`,
								type: 'address-autocomplete',
								doNotSubmit: true,
								required: true,
								requiredMessage: addressInvalidErrorMessage,
								disabled: isFormDisabled,
								data: {
									label: addressLabel,
									fullWidth: true,
									emptyResultLabel,
									noMatchLabel,
								},
								validators: [
									{
										type: 'required',
										message: addressInvalidErrorMessage,
									},
								],
							},
						],
						rules: [
							{
								property: 'shown',
								when: {
									all: [
										{
											eq: {
												fieldPath: `${addressPathPrefix}.${ADDRESS.COUNTRY}`,
												values: [AUSTRALIA_COUNTRY_CODE, ''],
											},
										},
										{
											not: {
												eq: {
													fieldPath: `${addressPathPrefix}.${ADDRESS.ADDRESS_LOOKUP}`,
													values: [MANUAL_ADDRESS],
												},
											},
										},
									],
								},
							},
						],
					},
					{
						id: MANUAL_ADDRESS,
						type: 'section',
						data: { align: 'left' },
						fields: [
							{
								id: `${addressPathPrefix}.${ADDRESS.STREET_1}`,
								path: `${addressPathPrefix}.${ADDRESS.STREET_1}`,
								type: 'text',
								required: true,
								requiredMessage: addressInvalidErrorMessage,
								hasCloseIcon: true,
								enableButtonWithin: true,
								disabled: isFormDisabled,
								data: {
									fullWidth: true,
									label: street1Label,
								},
								validators: [
									{
										type: 'required',
										message: addressInvalidErrorMessage,
									},
									{
										type: 'street-address',
										message: getMessageByCode(
											CONTACT_DETAILS_ERRORS.INVALID_STREET_ADDRESS,
											errorMessages,
											'errorMessage',
										),
									},
								],
							},
							{
								id: `${addressPathPrefix}.${ADDRESS.STREET_2}`,
								path: `${addressPathPrefix}.${ADDRESS.STREET_2}`,
								type: 'text',
								hasCloseIcon: true,
								enableButtonWithin: true,
								disabled: isFormDisabled,
								data: {
									fullWidth: true,
									label: street2Label,
								},
								validators: [
									{
										type: 'street-address',
										message: getMessageByCode(
											CONTACT_DETAILS_ERRORS.INVALID_STREET_ADDRESS,
											errorMessages,
											'errorMessage',
										),
									},
								],
							},
							{
								id: 'suburbLayout',
								type: 'sub-layout',
								data: { noMargin: true },
								fields: [
									{
										id: `${addressPathPrefix}.${ADDRESS.SUBURB}`,
										path: `${addressPathPrefix}.${ADDRESS.SUBURB}`,
										type: 'text',
										required: true,
										requiredMessage: addressInvalidErrorMessage,
										hasCloseIcon: true,
										enableButtonWithin: true,
										disabled: isFormDisabled,
										data: {
											label: suburbLabel,
										},
										validators: [
											{
												type: 'required',
												message: addressInvalidErrorMessage,
											},
											{
												type: 'suburb-address',
												message: getMessageByCode(
													CONTACT_DETAILS_ERRORS.INVALID_SUBURB,
													errorMessages,
													'errorMessage',
												),
											},
										],
										rules: [
											{
												property: 'required',
												when: {
													eq: {
														fieldPath: `${addressPathPrefix}.${ADDRESS.COUNTRY}`,
														values: [AUSTRALIA_COUNTRY_CODE],
													},
												},
											},
										],
									},
								],
							},
							{
								id: STATE_POSTCODE,
								type: 'sub-layout',
								data: {
									gap: 0,
									noMargin: true,
									noStack: true,
								},
								fields: [
									{
										id: 'stateLayout',
										type: 'sub-layout',
										data: {
											layout: '1/1',
											gap: { y: 0 },
											noMargin: true,
											noStack: true,
											rightPadding: 5,
										},
										fields: [
											{
												// Todo: adding a string after id will fix the "State" not displaying issue
												// need to find out root cause
												id: `${addressPathPrefix}.${ADDRESS.STATE}Aus`,
												path: `${addressPathPrefix}.${ADDRESS.STATE}`,
												type: 'select',
												required: true,
												requiredMessage: addressInvalidErrorMessage,
												disabled: isFormDisabled,
												data: {
													label: stateLabel,
													options: states,
												},
												validators: [
													{
														type: 'required',
														message: addressInvalidErrorMessage,
													},
												],
												rules: [
													{
														property: 'shown',
														when: {
															eq: {
																fieldPath: `${addressPathPrefix}.${ADDRESS.COUNTRY}`,
																values: [AUSTRALIA_COUNTRY_CODE],
															},
														},
													},
												],
											},
											{
												// NOTE: id must be unique even though they won't show up at same time
												id: `${addressPathPrefix}.${ADDRESS.STATE}Intel`,
												path: `${addressPathPrefix}.${ADDRESS.STATE}`,
												type: 'text',
												hasCloseIcon: true,
												enableButtonWithin: true,
												disabled: isFormDisabled,
												data: {
													label: stateLabel,
												},
												rules: [
													{
														property: 'shown',
														when: {
															not: {
																eq: {
																	fieldPath: `${addressPathPrefix}.${ADDRESS.COUNTRY}`,
																	values: [AUSTRALIA_COUNTRY_CODE],
																},
															},
														},
													},
												],
											},
										],
									},
									{
										id: ADDRESS.POSTCODE,
										type: 'sub-layout',
										data: {
											gap: { y: 0 },
											noMargin: true,
											noStack: true,
											leftPadding: 5,
										},
										fields: [
											{
												id: `${addressPathPrefix}.${ADDRESS.POSTCODE}`,
												path: `${addressPathPrefix}.${ADDRESS.POSTCODE}`,
												type: 'text',
												hasCloseIcon: true,
												enableButtonWithin: true,
												disabled: isFormDisabled,
												validators: [
													{
														type: 'postcode',
														options: {
															targetField: `${addressPathPrefix}.${ADDRESS.COUNTRY}`,
															postcodeErrorMessages: postcodeErrorMessages,
															addressPathPrefix: addressPathPrefix,
														},
													},
													{
														type: 'postcode-required',
														options: {
															targetField: `${addressPathPrefix}.${ADDRESS.COUNTRY}`,
															fieldName: `${addressPathPrefix}.${ADDRESS.POSTCODE}`,
															postcodeErrorMessages: postcodeErrorMessages,
														},
													},
												],
												data: {
													label: postcodeLabel,
													inputAttrs: {
														maxLength: 20,
														onInput: function(e: any) {
															e.target.value = e.target.value.replaceAll(
																/[^a-zA-Z0-9 -]/g,
																'',
															);
														},
													},
												},
											},
										],
									},
								],
							},
						],
						rules: [
							{
								property: 'shown',
								when: {
									any: [
										{
											eq: {
												fieldPath: `${addressPathPrefix}.${ADDRESS.ADDRESS_LOOKUP}`,
												values: ['manual'],
											},
										},
										{
											not: {
												eq: {
													fieldPath: `${addressPathPrefix}.${ADDRESS.COUNTRY}`,
													values: [AUSTRALIA_COUNTRY_CODE, ''],
												},
											},
										},
									],
								},
							},
						],
					},
				],
				// append rules for show hide of the additional address input groups
				rules,
			},
			...(!!additionalAddressLabel
				? [
						{
							id: ADDRESS.ADDITIONAL_ADDRESS,
							path: `${addressPathPrefix}.${ADDRESS.ADDITIONAL_ADDRESS}`,
							type: 'checkbox' as any,
							data: {
								options: [
									{
										label: additionalAddressLabel,
										value: true,
									},
								],
							},
						},
				  ]
				: []),
		];
	} else {
		return [
			...generateAddressFields(primaryAddress, undefined, isFormDisabled),
			...generateAddressFields(
				{
					...additionalAddress,
					rules: [
						{
							property: 'shown',
							when: {
								eq: {
									fieldPath: `${addressPathPrefix}.${ADDRESS.ADDITIONAL_ADDRESS}`,
									values: [true],
								},
							},
						},
					],
				},
				undefined,
				isFormDisabled,
			),
		];
	}
};

/**
 * Generate address fields config including fields and initial values.
 *
 * @param primaryAddress    - primary address config
 * @param additionalAddress - optional additional address config and only show when additionalAddress checkbox is check
 * @param isFormDisabled - this is to disable all the form fields. Helpful in preventing the user to hit enter key while form is still submitting to avoid mutliple form submission
 */
export const generateAddressFieldsConfig = (
	primaryAddress: IGenerateAddressFieldsConfig,
	additionalAddress?: IGenerateAddressFieldsConfig,
	isFormDisabled = false,
) => {
	const fields = generateAddressFields(
		primaryAddress,
		additionalAddress,
		isFormDisabled,
	);
	const initialAddressValues = {
		[ADDRESS.COUNTRY]: AUSTRALIA_COUNTRY_CODE,
		[ADDRESS.ADDRESS]: '',
		[ADDRESS.STREET_1]: '',
		[ADDRESS.STREET_2]: '',
		[ADDRESS.SUBURB]: '',
		[ADDRESS.STATE]: '',
		[ADDRESS.POSTCODE]: '',
	};
	const initialValues = {
		[primaryAddress.addressPathPrefix]: initialAddressValues,
		...(additionalAddress && {
			[additionalAddress.addressPathPrefix]: initialAddressValues,
		}),
	};

	return { fields, initialValues };
};
